import { DomOverlay } from './DomOverlay';
import { Haptics } from '../Haptics';

type PauseEvents = {
  resume: void;
  menu: void;
};

export interface PauseStats {
  score: number;
  combo: number;
  elapsedMs: number;
}

/**
 * PauseOverlay — DOM pause modal. Mirrors the GameOverOverlay structure
 * (glass panel, two CTAs) but with an entirely non-destructive primary action.
 *
 * Backdrop tap = Resume (for quick recovery). The only way to leave the run
 * is the explicit "Quit to Menu" secondary button.
 */
export class PauseOverlay extends DomOverlay<PauseEvents> {
  private stats: PauseStats;

  constructor(stats: PauseStats) {
    super();
    this.stats = stats;
  }

  protected render(): void {
    this.rootEl.innerHTML = '';
    this.rootEl.appendChild(this.buildContent());

    if (!document.getElementById('pause-overlay-styles')) {
      const style = this.el('style');
      style.id = 'pause-overlay-styles';
      style.textContent = PAUSE_STYLES;
      document.head.appendChild(style);
    }

    Haptics.light();
  }

  private buildContent(): HTMLDivElement {
    const wrap = this.el('div', 'pause-wrap');

    // Backdrop — tap-to-resume target
    const backdrop = this.el('div', 'pause-backdrop');
    backdrop.addEventListener('click', () => {
      Haptics.selection();
      this.emit('resume', undefined as never);
    });

    const panel = this.el('div', 'pause-panel glass-panel glass-panel--strong spring-in');
    // Swallow click on the panel so it doesn't bubble to the backdrop.
    panel.addEventListener('click', (e) => e.stopPropagation());

    const eyebrow = this.el('div', 'type-heading pause-eyebrow', 'PAUSED');
    const title = this.el('h1', 'type-display pause-title', 'Take a breath');
    panel.appendChild(eyebrow);
    panel.appendChild(title);

    // Stat readout
    const statsRow = this.el('div', 'pause-stats');
    statsRow.appendChild(this.buildStat('SCORE', this.stats.score.toLocaleString()));
    statsRow.appendChild(this.buildStat('TIME', this.formatTime(this.stats.elapsedMs)));
    if (this.stats.combo > 1) {
      statsRow.appendChild(this.buildStat('COMBO', `×${this.stats.combo}`));
    }
    panel.appendChild(statsRow);

    // Buttons
    const actions = this.el('div', 'pause-actions');
    const resume = this.el('button', 'btn btn--filled pause-btn', 'Resume');
    resume.addEventListener('click', () => {
      Haptics.selection();
      this.emit('resume', undefined as never);
    });
    const menu = this.el('button', 'btn btn--ghost pause-btn', 'Quit to Menu');
    menu.addEventListener('click', () => {
      Haptics.medium();
      this.emit('menu', undefined as never);
    });
    actions.appendChild(resume);
    actions.appendChild(menu);
    panel.appendChild(actions);

    wrap.appendChild(backdrop);
    wrap.appendChild(panel);
    return wrap;
  }

  private buildStat(label: string, value: string): HTMLDivElement {
    const block = this.el('div', 'pause-stat');
    block.appendChild(this.el('div', 'type-micro pause-stat__label', label));
    block.appendChild(this.el('div', 'type-numeric pause-stat__value', value));
    return block;
  }

  private formatTime(ms: number): string {
    const total = Math.max(0, Math.floor(ms / 1000));
    const mm = Math.floor(total / 60).toString().padStart(2, '0');
    const ss = (total % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  }
}

const PAUSE_STYLES = `
.pause-wrap {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 30;
}
.pause-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(5, 5, 7, 0.7);
  backdrop-filter: blur(10px) saturate(160%);
  -webkit-backdrop-filter: blur(10px) saturate(160%);
}
.pause-panel {
  position: relative;
  width: 100%;
  max-width: 360px;
  padding: 28px 26px 24px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 14px;
}
.pause-eyebrow {
  text-align: center;
  margin: 0;
  color: var(--accent-cyan);
  letter-spacing: 0.22em;
}
.pause-title {
  text-align: center;
  font-size: 30px;
  margin: 0 0 6px;
  letter-spacing: 0.01em;
}
.pause-stats {
  display: flex;
  justify-content: space-around;
  gap: 10px;
  padding: 10px 0 14px;
  border-top: 1px solid rgba(136, 146, 176, 0.12);
  border-bottom: 1px solid rgba(136, 146, 176, 0.12);
}
.pause-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.pause-stat__label {
  color: var(--text-secondary);
}
.pause-stat__value {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
}
.pause-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 6px;
}
.pause-btn { width: 100%; min-height: 44px; }
`;
